/**
 * Insights — Risk page
 *
 * R3 P1 fix 2026-05-13 — PR #430 added `/insights-module/risk` backend
 * (incidents, bySeverity, byType, blockedActions, failedAuditEvents,
 * daily trend) but no UI consumed it. Visiting /insights/risk 404'd in
 * the SPA. This page surfaces that endpoint as a dedicated Risk view.
 *
 * INSIGHTS-UI-SPEC ships 5 tabs and treats Risk as the Safety sub-tab
 * of Quality & Safety (§7.2). The Safety sub-tab is now wired to the
 * same `/risk` endpoint (see quality.tsx). This page exists so direct
 * `/insights/risk` URLs don't 404 and so the dedicated Risk surface
 * has room to grow.
 */

import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import {
  ShieldAlert, AlertTriangle,
} from 'lucide-react';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip,
  CartesianGrid, Legend,
} from 'recharts';
import { CHART_TOOLTIP } from '@/lib/chart-theme';
import {
  KpiCard, ChartCard, FilterBar, ActionItemsDrawer, EmptySection,
  filtersToParams,
  useInsightsSearchFilters, validateInsightsSearch,
} from '@/components/insights';
import { useInsightsRisk } from '@/lib/use-insights';

export const Route = createFileRoute('/insights/risk')({
  component: RiskTab,
  // INS-FILTER-PERSIST-001 — URL-bind filter state per resolved decision #9.
  validateSearch: validateInsightsSearch,
});

function RiskTab() {
  const [filters, setFilters] = useInsightsSearchFilters('/insights/risk');
  const [actionItemsOpen, setActionItemsOpen] = useState(false);

  // R3-P0 defect #1 — FilterBar filters propagate to backend. Risk
  // endpoint accepts the full filter set; dept filters Risk via the
  // user_id join; agentId / modelId pass through (no-op against
  // governance_incidents which has neither column).
  const filterParams = filtersToParams(filters);
  const range = filterParams.range;
  const { data: risk, isLoading } = useInsightsRisk(range, {
    department: filterParams.department,
    agentId: filterParams.agentId,
    modelId: filterParams.modelId,
  });

  const totalIncidents = risk?.totalIncidents ?? 0;
  const openIncidents = risk?.openIncidents ?? 0;
  const blockedActions = risk?.blockedActions ?? 0;
  const failedAuditEvents = risk?.failedAuditEvents ?? 0;
  const bySeverity = risk?.bySeverity ?? [];
  const byType = risk?.byType ?? [];
  const trend = risk?.trend ?? [];

  const trendData = trend.map((t) => ({
    date: t.date,
    Incidents: t.incidents,
    Blocked: t.blocked,
  }));

  return (
    <div className="flex flex-col h-full">
      <FilterBar
        filters={filters}
        onChange={setFilters}
        actionItemsCount={0}
        onOpenActionItems={() => setActionItemsOpen(true)}
      />

      {/* Pod-KK #613 — explicit custom-range fallback notice. */}
      {filterParams.customFallback && (
        <div
          role="status"
          className="mx-6 mt-3 rounded-md border border-amber-300/60 bg-amber-50 dark:bg-amber-950/30 px-3 py-2 text-xs text-amber-900 dark:text-amber-200"
        >
          Custom date ranges aren't supported yet — showing the last 7
          days. Pick a preset range to filter precisely.
        </div>
      )}

      <div className="flex-1 p-6 space-y-6">
        {/* KPIs */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <KpiCard
            label="Total incidents"
            icon={ShieldAlert}
            value={isLoading ? '—' : totalIncidents.toLocaleString()}
            numericValue={totalIncidents}
            subtitle={`Governance incidents in ${range}`}
            statusDot={totalIncidents === 0 ? 'emerald' : totalIncidents < 10 ? 'amber' : 'red'}
          />
          <KpiCard
            label="Open incidents"
            icon={AlertTriangle}
            value={isLoading ? '—' : openIncidents.toLocaleString()}
            numericValue={openIncidents}
            subtitle="Status = 'open'"
            statusDot={openIncidents === 0 ? 'emerald' : 'amber'}
          />
          <KpiCard
            label="Blocked actions"
            icon={ShieldAlert}
            value={isLoading ? '—' : blockedActions.toLocaleString()}
            numericValue={blockedActions}
            subtitle="audit_logs outcome IN ('blocked','denied')"
          />
          <KpiCard
            label="Failed audit events"
            icon={AlertTriangle}
            value={isLoading ? '—' : failedAuditEvents.toLocaleString()}
            numericValue={failedAuditEvents}
            subtitle="audit_logs outcome = 'failure'"
            statusDot={failedAuditEvents === 0 ? 'emerald' : failedAuditEvents < 5 ? 'amber' : 'red'}
          />
        </div>

        {/* Daily trend */}
        <ChartCard title={`Incidents & blocked actions (${range})`} icon={ShieldAlert}>
          {trendData.length === 0 ? (
            <EmptySection
              message="No incidents or blocked actions in the selected range."
              hint="Trend populates from governance_incidents + audit_logs as events accrue."
              minHeight="min-h-48"
            />
          ) : (
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={trendData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-border/40" vertical={false} />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 10 }}
                    tickFormatter={(v) =>
                      new Date(v).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                    }
                  />
                  <YAxis tick={{ fontSize: 10 }} width={40} />
                  <Tooltip {...CHART_TOOLTIP} />
                  <Legend wrapperStyle={{ fontSize: 11, paddingTop: 8 }} />
                  <Bar dataKey="Incidents" stackId="a" fill="rgb(168 85 247)" />
                  <Bar dataKey="Blocked" stackId="a" fill="rgb(239 68 68)" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </ChartCard>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <ChartCard title={`By severity (${range})`} icon={ShieldAlert}>
            {bySeverity.length === 0 ? (
              <EmptySection
                message="No incidents in the selected range."
                minHeight="min-h-48"
              />
            ) : (
              <div className="h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={bySeverity}
                    margin={{ top: 8, right: 8, left: 0, bottom: 0 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" className="stroke-border/40" vertical={false} />
                    <XAxis dataKey="severity" tick={{ fontSize: 10 }} />
                    <YAxis tick={{ fontSize: 10 }} width={40} />
                    <Tooltip {...CHART_TOOLTIP} />
                    <Bar dataKey="count" fill="rgb(99 102 241)" name="Incidents" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </ChartCard>

          <ChartCard title={`By type — top 10 (${range})`} icon={ShieldAlert}>
            {byType.length === 0 ? (
              <EmptySection
                message="No incident-type rollups in the selected range."
                minHeight="min-h-48"
              />
            ) : (
              <div className="space-y-1.5">
                {byType.slice(0, 10).map((t) => (
                  <div
                    key={t.incidentType}
                    className="flex items-center justify-between text-xs px-2 py-1.5 rounded border border-border"
                  >
                    <span className="truncate">{t.incidentType}</span>
                    <span className="font-semibold tabular-nums">{t.count}</span>
                  </div>
                ))}
              </div>
            )}
          </ChartCard>
        </div>
      </div>

      <ActionItemsDrawer
        open={actionItemsOpen}
        onOpenChange={setActionItemsOpen}
        items={[]}
      />
    </div>
  );
}
